import { Inject, Injectable } from '@angular/core';

import { UserDetailsDto,
  Language,
  LanguageCode,
  SupporterFiltersCached,
  CategoryBase,
  CategoryDto,
  CategoryTranslationCollectionDto,
  LanguageTranslationDto } from '../shared/model';
import { DataService } from '../data.service';

import { Observable, throwError, forkJoin, from } from 'rxjs';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

@Injectable()
export class SharedService {

  public currentFilters: {[key: string]: SupporterFiltersCached} = {};

  private loggedInUser: UserDetailsDto;
  private currentLanguage: Language = <Language>{};
  private translatedCategories: CategoryBase[] = [];
  private categories: CategoryDto[];
  private categoryTranslations: CategoryTranslationCollectionDto[];
  private allLanguages: Language[] = [];
  private translatedLanguages: Language[] = [];
  private languageTranslations: Array<LanguageTranslationDto> = [];

  private defaultLanguage: Language = { Id: '', Name: 'English', LanguageCode: LanguageCode.en};

  private flagSrc = [{
    'name': 'English',
    'code': 'en',
    'src': 'assets/GDPR/EN.svg'
  }, {
    'name': 'French',
    'code': 'fr',
    'src': 'assets/GDPR/FR.svg'
  }, {
    'name': 'German',
    'code': 'de',
    'src': 'assets/GDPR/DE.svg'
  }, {
    'name': 'Spanish',
    'code': 'es',
    'src': 'assets/GDPR/ES.svg'
  }, {
    'name': 'Dutch',
    'code': 'nl',
    'src': 'assets/GDPR/NL.svg'
  }, {
    'name': 'Italian',
    'code': 'it',
    'src': 'assets/GDPR/IT.svg'
  }];

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private dataService: DataService) {

    let currentLang = localStorage.getItem('currLanguage');
    if (currentLang !== undefined && currentLang !== 'undefined' && currentLang !== null) {
      const language = JSON.parse(currentLang);
      if (language != null) {
        this.currentLanguage = language;
      }
    } else {
      this.currentLanguage = this.defaultLanguage;
    }

    this.getLanguagesTranslations()
        .subscribe(() => {});

    if (localStorage.getItem('currUser') !== undefined && localStorage.getItem('currUser') !== 'undefined') {
      const currentUser = JSON.parse(localStorage.getItem('currUser'));
      if (currentUser != null) {
        this.loggedInUser = currentUser;
      } else {
        this.getUserDetails();
      }
    }

    if (localStorage.getItem('currentFilters') !== undefined && localStorage.getItem('currentFilters') !== 'undefined') {
      const cachedFilters = JSON.parse(localStorage.getItem('currentFilters'));
      if (cachedFilters != null) {
        this.currentFilters = cachedFilters;
      }
    }

    if (this.translatedCategories.length === 0 && !!this.currentLanguage) {
      this.getCategoryTranslations()
        .subscribe(() => {});
    }
  }

  setCurrentUser(user: UserDetailsDto) {
    this.loggedInUser = user;
    localStorage.setItem('currUser', JSON.stringify(this.loggedInUser));
  }

  setCurrentLanguage(language: Language) {
    this.currentLanguage = language;
    localStorage.setItem('currLanguage', JSON.stringify(this.currentLanguage));
  }

  getCurrentLanguage(): Language {
    if(!this.currentLanguage || this.currentLanguage === null || this.currentLanguage === undefined) {
      return this.defaultLanguage;
    }
    return this.currentLanguage;
  }

  getFlags() {
    return this.flagSrc;
  }

  getTranslatedCategories(): CategoryBase[] {
    return this.translatedCategories;
  }

  getTranslatedLanguages(): Language[] {
    return this.translatedLanguages;
  }

  getLanguages(): Observable<any> {
    return this.dataService.get('Languages/GetLanguages', {});
  }

  getEnglishLanguage(): Observable<any> {
    return this.dataService.get('Languages/GetEnglish', {});
  }

  getLanguagesTranslations(): Observable<any> {

      return forkJoin(
        this.dataService.get('Translations/GetLanguageTranslations', {}),
        this.dataService.get('Languages/GetLanguages', {}))
          .map(([translations, languages]) => {
          this.allLanguages = languages;
          this.languageTranslations = translations;
          this.adjustLanguageTranslationForSelectedLanguage();
      });
  }

  getCategoryTranslations(): Observable<any> {
    return forkJoin(
      [this.dataService.get('Category/GetCategories', {}),
      this.dataService.get('Translations/GetAllCategoryTranslations', {})]
    ).map(([categories, translations]) => {
      this.categories = categories;
      this.categoryTranslations = translations;
      this.adjustCategoryTranslationsForSelectedLanguage();
    });
  }

  getSupporters(): Observable<any> {
    return this.dataService.get('Supporter/GetSupporters', {});
  }

  getUserDetails(): Observable<UserDetailsDto> {
    return forkJoin([
      from(this.oktaAuth.getUser()),
      this.dataService.get('User/GetUserDetails', {})
    ]).pipe(
      map(([userClaims, user]) => {
        user.LastName = userClaims.family_name;
        user.FirstName = userClaims.given_name;

        this.setCurrentUser(user);
        return user;
      }),
      catchError((error: any) => {
        return throwError(error || 'Server error');
      }));
  }

  setLanguageIndicator(): Observable<boolean> {
    return this.dataService.get('User/SetLanguageIndicator', {}).pipe(
      catchError((error: any) => {
        return throwError(error || 'Server error');
      }));
  }

  hasUserSetLanguage(): Observable<boolean> {
    return this.dataService.get('User/HasUserSetLanguage', {}).pipe(
      catchError((error: any) => {
        return throwError(error || 'Server error');
      }));
  }

  getLoggedUser(): UserDetailsDto {
    return this.loggedInUser;
  }

  getUserRole(): number {
    return this.loggedInUser.UserRole;
  }

  getPermissionsLevel(): Observable<number> {
    return Observable.create((observer) => {
      this.emitPermissionsLevel(this.loggedInUser, observer);
    });
  }

  setCurrentFilters(userEmail: string, filters: SupporterFiltersCached ) {
    this.currentFilters[userEmail] = filters;
    localStorage.setItem('currentFilters', JSON.stringify(this.currentFilters));
  }

  getCurrentFilters(userEmail: string): SupporterFiltersCached {
    return this.currentFilters[userEmail];
  }

  translateCategories(categories: Array<CategoryDto>): Array<CategoryDto> {
    let translatedCategoryDtos: Array<CategoryDto> = [];

    translatedCategoryDtos = categories.map(c => {
      const translation = this.translatedCategories.find(t => t.Id === c.Id);
      c.Name = translation && translation.Name && translation.Name.trim() !== '' ? translation.Name : c.Name;
      c.Description = translation && translation.Description.trim() !== '' ? translation.Description : c.Description;
      return c;
    });

    return translatedCategoryDtos;
  }

  translateLanguage(language: Language): Language {
    const translation = this.translatedLanguages.find(l => l.Id === language.Id);
    return translation !== undefined ? translation : language;
  }

  areLanguagesTranslated(): boolean {
    return this.translatedLanguages && this.translatedLanguages.length > 0;
  }

  private emitPermissionsLevel(user: UserDetailsDto, observer: any) {
    if (user === undefined) {
      observer.next(0);
    } else {
      observer.next(user.UserRole);
    }
    observer.complete();
  }

  private adjustCategoryTranslationsForSelectedLanguage() {
    this.translatedCategories = [];
    this.categories.forEach(c => {

      if (!!this.categoryTranslations) {
        const translatedCategory = this.categoryTranslations.find(col => col.CategoryId === c.Id);

        if (!!translatedCategory) {
          const translatedToLanguage = translatedCategory.Translations.find(t => t.LanguageId === this.currentLanguage.Id);

          if (translatedToLanguage) {
            this.translatedCategories.push(<CategoryBase>{
              Id: c.Id,
              Name: translatedToLanguage.Name,
              Description: translatedToLanguage.Description
            });
          } else {
            this.translatedCategories.push(<CategoryBase>(c));
          }
        } else {
          this.translatedCategories.push(<CategoryBase>(c));
        }
      } else {
        this.translatedCategories = this.categories.map(category => {
          return <CategoryBase>(category);
        });
      }
    });
  }

  private adjustLanguageTranslationForSelectedLanguage() {
    this.translatedLanguages = [];
    this.allLanguages.forEach(l => {
      if (this.languageTranslations) {
        const langTranslations = this.languageTranslations.filter(lang => lang.TranslatedLanguageId === l.Id);

        if (langTranslations) {
          const translatedLanguage = langTranslations.find(lang => lang.LanguageId === this.currentLanguage.Id);

          if (translatedLanguage) {
            this.translatedLanguages.push(<Language>{
              Id: l.Id,
              Name: translatedLanguage.Translation,
              LanguageCode: l.LanguageCode
            });
          } else {
            this.translatedLanguages.push(l);
          }
        } else {
          this.translatedLanguages.push(l);
        }
      } else {
        this.translatedLanguages = [...this.allLanguages];
      }
    });
  }
}
